import React, { ReactNode, FunctionComponent } from 'react';
import idx from 'idx';
import { FormattedMessage } from 'react-intl';

import { ProgressTour_tradeFirm } from '../Header/__generated__/ProgressTour_tradeFirm.graphql';

type TradeFirmType = ProgressTour_tradeFirm | null;

export type TaskState = {
    order: number;
    completed: boolean;
    required: boolean;
    name: string;
    label?: ReactNode;
    incompleteMessage?: ReactNode;
};

const BackgroundLabel: FunctionComponent = () => (
    <FormattedMessage
        id="abt.designProfile.header.progress.background.label"
        defaultMessage="Add a background photo"
    />
);
const BackgroundMessage: FunctionComponent = () => (
    <FormattedMessage
        id="abt.designProfile.header.progress.background.message"
        defaultMessage="Please add a background to your profile"
    />
);
const ProfileLabel: FunctionComponent = () => (
    <FormattedMessage
        id="abt.designProfile.header.progress.profile.label"
        defaultMessage="Add a profile photo"
    />
);
const ProfileMessage: FunctionComponent = () => (
    <FormattedMessage
        id="abt.designProfile.header.progress.profile.message"
        defaultMessage="Please add a 350x350 photo in the profile photo section."
    />
);
const LocationLabel: FunctionComponent = () => (
    <FormattedMessage
        id="abt.designProfile.header.progress.location.label"
        defaultMessage="Add your location"
    />
);
const LocationMessage: FunctionComponent = () => (
    <FormattedMessage
        id="abt.designProfile.header.progress.location.message"
        defaultMessage="Please add your firm's address."
    />
);
const EmailLabel: FunctionComponent = () => (
    <FormattedMessage
        id="abt.designProfile.header.progress.email.label"
        defaultMessage="Add your email"
    />
);
const EmailMessage: FunctionComponent = () => (
    <FormattedMessage
        id="abt.designProfile.header.progress.email.message"
        defaultMessage="Please add the email address customers can use to contact your firm."
    />
);
const AboutLabel: FunctionComponent = () => (
    <FormattedMessage
        id="abt.designProfile.header.progress.about.label"
        defaultMessage="Tell us about your firm"
    />
);
const AboutMessage: FunctionComponent = () => (
    <FormattedMessage
        id="abt.designProfile.header.progress.about.message"
        defaultMessage='Please add a description in the "About" section.'
    />
);
const ProjectsLabel: FunctionComponent = () => (
    <FormattedMessage
        id="abt.designProfile.header.progress.projects.label"
        defaultMessage="Add a project"
    />
);
const ProjectsMessage: FunctionComponent = () => (
    <FormattedMessage
        id="abt.designProfile.header.progress.projects.message"
        defaultMessage="Please add a project to your profile."
    />
);
export const getBackgroundTask = (tradeFirm: TradeFirmType): TaskState => {
    const completed = !!idx(tradeFirm, o => o.publicProfile.splashPhoto);
    return {
        order: 1,
        name: 'background',
        required: true,
        completed,
        label: <BackgroundLabel />,
        incompleteMessage: <BackgroundMessage />,
    };
};

export const getProfileTask = (tradeFirm: TradeFirmType): TaskState => {
    const completed = !!idx(tradeFirm, o => o.publicProfile.profilePhoto);
    return {
        order: 2,
        name: 'profile',
        required: true,
        completed,
        label: <ProfileLabel />,
        incompleteMessage: <ProfileMessage />,
    };
};

export const getLocationTask = (tradeFirm: TradeFirmType): TaskState => {
    const hasZipCode = !!idx(tradeFirm, o => o.publicProfile.address.zipCode);
    const hasCountry = !!idx(tradeFirm, o => o.publicProfile.address.country);
    const completed = hasZipCode && hasCountry;

    return {
        order: 3,
        name: 'location',
        required: true,
        completed,
        label: <LocationLabel />,
        incompleteMessage: <LocationMessage />,
    };
};

export const getEmailTask = (tradeFirm: TradeFirmType): TaskState => {
    const completed = !!idx(tradeFirm, o => o.publicProfile.email);

    return {
        order: 4,
        name: 'email',
        required: true,
        completed,
        label: <EmailLabel />,
        incompleteMessage: <EmailMessage />,
    };
};

export const getAboutTask = (tradeFirm: TradeFirmType): TaskState => {
    let about = idx(tradeFirm, o => o.publicProfile.about) || '';

    try {
        about = JSON.parse(about);
    } catch (e) {
        /* swallow error */
    }

    const completed = !!about.length;

    return {
        order: 5,
        name: 'about',
        required: true,
        completed,
        label: <AboutLabel />,
        incompleteMessage: <AboutMessage />,
    };
};

export const getProjectsTask = (tradeFirm: TradeFirmType): TaskState => {
    const projects = (idx(tradeFirm, o => o.publicProfile.designProjectsCover) || []).filter(
        project => idx(project, o => o.status) === 'ACTIVE'
    );

    const completed = projects.length >= 1;

    return {
        order: 6,
        name: 'projects',
        required: true,
        completed,
        label: <ProjectsLabel />,
        incompleteMessage: <ProjectsMessage />,
    };
};
